import type { Annotation } from "@rowan/prisma";
import { useEffect, useRef } from "react";

type Props = {
  annotations: Annotation[];
  containerRef: React.RefObject<HTMLElement | null>;
  applyHighlights: (annotations: Annotation[], focusedId?: string) => void;
  applyHighlight: (range: Range, id: string) => void;
  clearBorder: (id?: string) => void;
  clearHighlight: (id?: string) => void;
  deserializeRange: (annotation: Annotation["range"]) => Range | null;
  htmlChunks?: Record<number, string>;
  annotationsOpen?: boolean;
  focusedId?: string;
  updateMenus?: () => void;
};

export const useHighlightObserver = ({
  annotations,
  containerRef,
  applyHighlight,
  applyHighlights,
  clearHighlight,
  clearBorder,
  deserializeRange,
  htmlChunks,
  annotationsOpen,
  focusedId,
  updateMenus,
}: Props) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const observedSectionsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!annotationsOpen) {
      clearBorder();
      clearHighlight();
      return;
    }

    const setupObservers = (container: HTMLElement) => {
      if (!resizeObserverRef.current) {
        resizeObserverRef.current = new ResizeObserver(() => {
          applyHighlights(annotations, focusedId);
          updateMenus?.();
        });
      }

      const localObservedSections = observedSectionsRef.current;

      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const sectionId = entry.target.id;
              const isVisible = entry.isIntersecting;
              const matches = annotations.filter((a) =>
                a.sectionId.includes(sectionId),
              );

              matches.forEach((annotation) => {
                const id = `${annotation.publicationId}_${annotation.id}`;
                const section = document.getElementById(annotation.sectionId);
                if (!section) return;

                if (isVisible) {
                  const range = deserializeRange(annotation.range);
                  if (range) applyHighlight(range, id);
                  resizeObserverRef.current?.observe(section);
                  updateMenus?.();
                } else {
                  clearHighlight(id);
                  clearBorder(id);
                  resizeObserverRef.current?.unobserve(section);
                }
              });
            });
          },
          {
            root: container,
            rootMargin: "200px",
            threshold: 0.01,
          },
        );
      }

      const observer = observerRef.current;

      const tryObserveNewSections = () => {
        annotations.forEach((annotation) => {
          const sectionId = annotation.sectionId;
          if (!localObservedSections.has(sectionId)) {
            const el = document.getElementById(sectionId);
            if (el) {
              observer.observe(el);
              localObservedSections.add(sectionId);
            }
          }
        });
      };

      tryObserveNewSections();

      if (!mutationObserverRef.current) {
        mutationObserverRef.current = new MutationObserver(() => {
          tryObserveNewSections();
        });

        mutationObserverRef.current.observe(container, {
          childList: true,
          subtree: true,
        });
      }
    };

    const cleanupObservers = () => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      mutationObserverRef.current?.disconnect();
      mutationObserverRef.current = null;

      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      observedSectionsRef.current.clear();
    };
    let raf: number;

    const waitForContainer = () => {
      const container = containerRef.current;
      if (!container) {
        raf = requestAnimationFrame(waitForContainer);
        return;
      }

      setupObservers(container);
    };

    waitForContainer();

    return () => {
      cancelAnimationFrame(raf);
      cleanupObservers();
    };
  }, [
    annotations,
    applyHighlight,
    applyHighlights,
    clearHighlight,
    deserializeRange,
    htmlChunks,
    containerRef,
    annotationsOpen,
    clearBorder,
    focusedId,
    updateMenus,
  ]);
};
